import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  customColors,
  customColorsRaw,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import React, { useMemo } from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { format } from 'src/lib/format';
import { Resolution } from 'src/lib/graphqlTypes/types';

import { FieldError } from '../../useCurrentField';

interface Props {
  loading?: boolean;
  errors?: FieldError[];
  resolution: Resolution;
  showSubscriptionMissingInfo: boolean;
  showClientFiltersWarning: boolean;
}

type ErrorMetricType = 'total_errors' | 'percentage';

export const FieldPageErrorsChart = ({
  loading,
  errors,
  resolution,
  showSubscriptionMissingInfo,
  showClientFiltersWarning,
}: Props) => {
  const noErrors: boolean = useMemo(
    () => Boolean(errors?.every(({ errorCount }) => errorCount === 0)),
    [errors],
  );

  const [toggleValue, setToggleValue] =
    React.useState<ErrorMetricType>('total_errors');

  const trackCustomEvent = useTrackCustomEvent();

  const stacks: LineChartStackConfig<FieldError>[] = useMemo(
    () => [
      {
        colorGradientTop: customColors.bg['error-selected'],
        colorGradientBottom: `rgb(${customColorsRaw.bg['error-selected']} / 0)`,
        primaryColor: customColors.border.error,
        cursorColor: customColors.brand.accent,
        label: 'Errors',
        data: errors ?? [],
        getValue: ({ timestamp, errorCount, percentage }: FieldError) => ({
          timestamp,
          value: toggleValue === 'percentage' ? percentage : errorCount,
        }),
      },
    ],
    [errors, toggleValue],
  );

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          <Select
            value={toggleValue}
            onChange={(event) => {
              const newValue =
                event.target.value === 'total_errors'
                  ? 'total_errors'
                  : 'percentage';
              trackCustomEvent({
                action: 'user_action',
                category: 'Field Insights',
                label: `Change errors chart mode: ${newValue}`,
              });
              setToggleValue(newValue);
            }}
            feel="flat"
            className="self-center font-heading text-xl font-medium text-heading"
            placement="bottom-start"
          >
            <option
              className="font-normal"
              value={'total_errors' as ErrorMetricType}
            >
              Error count
            </option>
            <option
              className="font-normal"
              value={'percentage' as ErrorMetricType}
            >
              Error percentage
            </option>
          </Select>
          <span className="self-center">
            <DateRangeBadge />
          </span>
          {showSubscriptionMissingInfo || showClientFiltersWarning ? (
            <Tooltip
              label={
                <ul
                  className={classnames({
                    'list-inside list-disc':
                      showSubscriptionMissingInfo && showClientFiltersWarning,
                  })}
                >
                  {showSubscriptionMissingInfo && (
                    <li data-testid="error-graph-subscriptions-warning">
                      This chart does not yet include any field execution errors
                      from subscription operations.
                    </li>
                  )}
                  {showClientFiltersWarning && (
                    <li data-testid="error-graph-clients-warning">
                      Showing data from all clients. This chart does not
                      currently reflect any client filters that have been
                      selected.
                    </li>
                  )}
                </ul>
              }
            >
              <IconWarning
                className="ml-2 self-center text-icon-warning"
                data-testid="error-graph-warning-trigger"
              />
            </Tooltip>
          ) : null}
        </CardTitle>
        <CardDescription className="pl-3" data-testid="displayed-errors-msg">
          {toggleValue === 'total_errors'
            ? 'The number of times an operation has encountered an error on this field.'
            : 'The percentage of executions that encountered an error when resolving this field.'}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-10 text-center">
            <Loading />
          </div>
        ) : noErrors ? (
          <NoData
            className="flex h-56 items-center justify-center"
            message="There are no errors for this field in this time period."
          />
        ) : (
          <LineChart
            className="h-56"
            stacked
            formatValue={
              toggleValue === 'percentage'
                ? (_, value) => format.percent(value)
                : undefined
            }
            stacks={stacks}
            resolution={resolution}
            allowSingleXAxisValue
            tooltipLabelAs={<InsightsLineChartTooltip />}
            hideCursorArrow
          />
        )}
      </CardBody>
    </Card>
  );
};
